/**
 * Shared authenticated query for the learner diagnostic profile.
 *
 * @module useDiagnosticProfile
 */

import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";
import {
  getDiagnosticProfile,
  type DiagnosticProfile,
} from "@/lib/diagnosticApi";

export const DIAGNOSTIC_PROFILE_QUERY_KEY = ["diagnostic-profile"] as const;

export const diagnosticProfileQueryKey = (userId: string | null | undefined) =>
  userId
    ? ([...DIAGNOSTIC_PROFILE_QUERY_KEY, userId] as const)
    : DIAGNOSTIC_PROFILE_QUERY_KEY;

export function useDiagnosticProfile(enabled: boolean = true) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const query = useQuery<DiagnosticProfile>({
    queryKey: diagnosticProfileQueryKey(userId),
    queryFn: getDiagnosticProfile,
    enabled: enabled && Boolean(userId),
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
